import { useState } from "react";
import { Download, X, Share, PlusSquare, MoreVertical } from "lucide-react";
import { useInstall } from "../context/InstallContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const DISMISS_KEY = "lvb-install-dismissed";

export const InstallHelpDialog = () => {
  const { showIosHelp, setShowIosHelp, ios } = useInstall();
  return (
    <Dialog open={showIosHelp} onOpenChange={setShowIosHelp}>
      <DialogContent className="bg-white max-w-sm" data-testid="pwa-install-help-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Add LVB Hostel to your home screen</DialogTitle><DialogDescription>Install it like an app — opens full screen from your phone.</DialogDescription></DialogHeader>
        {ios ? (
          <ol className="space-y-4 text-stone-700">
            <li className="flex gap-3"><Share className="h-5 w-5 text-amber-700 shrink-0" />Tap the <b>Share</b> button at the bottom of Safari.</li>
            <li className="flex gap-3"><PlusSquare className="h-5 w-5 text-amber-700 shrink-0" />Scroll and choose <b>Add to Home Screen</b>.</li>
            <li className="flex gap-3"><Download className="h-5 w-5 text-amber-700 shrink-0" />Tap <b>Add</b>. The LVB app icon appears on your phone.</li>
          </ol>
        ) : (
          <ol className="space-y-4 text-stone-700">
            <li className="flex gap-3"><MoreVertical className="h-5 w-5 text-amber-700 shrink-0" />Open the browser menu (⋮) at the top right.</li>
            <li className="flex gap-3"><Download className="h-5 w-5 text-amber-700 shrink-0" />Tap <b>Install app</b> or <b>Add to Home screen</b>.</li>
            <li className="flex gap-3"><PlusSquare className="h-5 w-5 text-amber-700 shrink-0" />Confirm. The LVB app icon appears on your phone.</li>
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const InstallButton = ({ className = "", label = "Install app" }) => {
  const { canInstall, install } = useInstall();
  if (!canInstall) return null;
  return (
    <button onClick={install} data-testid="pwa-install-button" className={`inline-flex items-center gap-2 ${className}`}>
      <Download className="h-4 w-4" />{label}
    </button>
  );
};

export default function InstallBanner({ withNav }) {
  const { canInstall, install } = useInstall();
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");
  if (!canInstall || dismissed) return null;
  const close = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };
  return (
    <div data-testid="pwa-install-banner"
      className={`md:hidden fixed left-3 right-3 z-40 bg-stone-50 border-2 border-amber-300 rounded-lg shadow-2xl p-3.5 flex items-center gap-3 fade-up ${withNav ? "bottom-[calc(4.75rem+env(safe-area-inset-bottom))]" : "bottom-[calc(1rem+env(safe-area-inset-bottom))]"}`}>
      <img src="/brand/icon-192.png" alt="LVB app" className="h-11 w-11 rounded-lg border border-stone-200" />
      <div className="flex-1 min-w-0">
        <p className="font-display font-bold text-stone-900 leading-tight">Get the LVB Hostel app</p>
        <p className="text-xs text-stone-600 font-typewriter truncate">The room changes. The bond doesn't.</p>
      </div>
      <button onClick={install} data-testid="pwa-install-banner-button" className="bg-amber-700 hover:bg-amber-800 text-white text-xs font-semibold px-3 py-2 rounded-md">Install</button>
      <button onClick={close} data-testid="pwa-install-dismiss" className="text-stone-400 hover:text-stone-700 p-1"><X className="h-4 w-4" /></button>
    </div>
  );
}
